// ═══════════════════════════════════════════════════════════════
// 📘 MÓDULO 9 - UNIDAD 4: PHRASAL VERBS - RELATIONSHIPS
// ═══════════════════════════════════════════════════════════════
// ✅ Biblia v4.1 - Context a nivel SET, campo "questions"
// ✅ Progresión: A2+ → B2+ (informal relationships → complex dynamics)
// ✅ Narrativa: Personal & Professional Relationships
// ═══════════════════════════════════════════════════════════════

export const M9_UNIT_5 = {
  id: "m9-u5-phrasal-verbs",
  title: "Unidad 4: Phrasal Verbs - Relationships",
  topic: "Master three-word phrasal verbs to talk about personal and professional relationships, understand informal English, and express complex social dynamics naturally.",
  iconType: "users",

  sets: [
    // ───────────────────────────────────────────────────────────
    // 💙 SET 1: Positive Relationships (A2+)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u5-s1",
      title: "Set 1: Positive Relationships",
      icon: "💙",
      context: `**City Office - Positive Workplace Dynamics**

Los THREE-WORD PHRASAL VERBS son combinaciones de verbo + partícula + preposición. Se usan mucho en inglés informal para hablar de relaciones.

**City Office - Positive Workplace Dynamics**

THREE-WORD PHRASAL VERBS are verb + particle + preposition combinations. They're used a lot in informal English to talk about relationships.`,

      questions: [
        {
          type: "match",
          q: "**Match: Phrasal Verb → Meaning**",
          pairs: [
            { left: "look up to someone", right: "admire and respect them" },
            { left: "get along with someone", right: "have a good relationship" },
            { left: "catch up with someone", right: "meet after time apart" },
            { left: "keep up with someone", right: "stay at their level" }
          ],
          successTip: "💙 **RELATIONSHIP VERBS!** These are super common in everyday English worldwide! Native speakers use them constantly!"
        },
        {
          type: "mc",
          q: "**Sarah:** 'My boss is amazing. I really ___ her.' (admire)",
          options: ["look up to", "look down on", "look forward to", "look out for"],
          a: "look up to",
          successTip: "👏 **LOOK UP TO!** When you admire someone professionally or personally! Common in workplace conversations globally!"
        },
        {
          type: "mc",
          q: "**Tom:** 'I ___ my coworkers. We work well together.' (have good relationship)",
          options: ["get along with", "fall out with", "catch up with", "put up with"],
          a: "get along with",
          successTip: "🤝 **GET ALONG WITH!** Essential for describing work relationships! Used in offices worldwide!"
        },
        {
          type: "input",
          q: "**Maria:** 'Let's ___ ___ coffee this weekend. I haven't seen you in ages!' (meet after time)",
          alts: ["catch up", "meet up"],
          a: "catch up",
          successTip: "☕ **CATCH UP!** Informal way to suggest meeting! Common in social invitations everywhere!"
        },
        {
          type: "mc",
          q: "**Daniel:** 'My sister runs marathons. I can barely ___ her.' (stay at same level)",
          options: ["keep up with", "get along with", "look up to", "put up with"],
          a: "keep up with",
          successTip: "🏃 **KEEP UP WITH!** Used for physical pace or professional competitiveness! Global business term!"
        },
        {
          type: "order",
          q: "**Traduce:** 'Realmente admiro a mi mentor.'",
          words: ["I", "really", "look", "up", "to", "my", "mentor"],
          extra: ["down", "for", "at"],
          a: ["I", "really", "look", "up", "to", "my", "mentor"],
          successTip: "💡 **Pattern: LOOK UP TO + person** = express admiration professionally!"
        },
        {
          type: "mc",
          q: "**Emma:** 'I haven't seen Lisa in months. We need to ___ soon.'",
          options: ["catch up", "look up", "give up", "make up"],
          a: "catch up",
          successTip: "📅 **CATCH UP!** Short form common in text messages! Works globally in informal communication!"
        },
        {
          type: "input",
          q: "**Manager:** 'How do you ___ ___ ___ the new team members?'",
          alts: ["get along with"],
          a: "get along with",
          successTip: "🏢 **GET ALONG WITH!** Common HR question in interviews worldwide! Shows interpersonal skills!"
        },
        {
          type: "mc",
          q: "**Jake:** 'My younger brother really ___ our dad. He wants to be just like him.'",
          options: ["looks up to", "looks down on", "looks forward to", "looks after"],
          a: "looks up to",
          successTip: "👨‍👦 **LOOKS UP TO!** Family relationships! Common way to describe role models globally!"
        },
        {
          type: "mc",
          q: "**Professor:** 'Can you ___ the fastest students in the class?'",
          options: ["keep up with", "catch up with", "get along with", "put up with"],
          a: "keep up with",
          successTip: "📚 **KEEP UP WITH!** Academic context! Used in schools and universities worldwide!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 💔 SET 2: Negative Relationships (A2+)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u5-s2",
      title: "Set 2: Negative Relationships",
      icon: "💔",
      context: `**Office Conflicts - Workplace Tensions**

Algunos phrasal verbs expresan relaciones NEGATIVAS: conflictos, actitudes superiores, y problemas interpersonales.

**Office Conflicts - Workplace Tensions**

Some phrasal verbs express NEGATIVE relationships: conflicts, superior attitudes, and interpersonal problems.`,

      questions: [
        {
          type: "match",
          q: "**Match: Negative Phrasal Verbs → Meanings**",
          pairs: [
            { left: "look down on someone", right: "think you're better than them" },
            { left: "fall out with someone", right: "have an argument and stop being friends" },
            { left: "put up with something", right: "tolerate something annoying" },
            { left: "grow apart from someone", right: "slowly become distant" }
          ],
          successTip: "💔 **NEGATIVE DYNAMICS!** Understanding these helps navigate difficult relationships anywhere in the world!"
        },
        {
          type: "mc",
          q: "**Sandra:** 'My colleague is so arrogant. He ___ everyone on the team.'",
          options: ["looks down on", "looks up to", "gets along with", "catches up with"],
          a: "looks down on",
          successTip: "😤 **LOOK DOWN ON!** Describes arrogance! Common in workplace complaints globally!"
        },
        {
          type: "mc",
          q: "**Mike:** 'I ___ my best friend after a huge argument. We haven't spoken in months.'",
          options: ["fell out with", "got along with", "looked up to", "kept up with"],
          a: "fell out with",
          successTip: "😢 **FELL OUT WITH!** Serious friendship breakdown! Used worldwide to describe conflicts!"
        },
        {
          type: "input",
          q: "**Boss:** 'I can't ___ ___ ___ his constant complaints anymore!'",
          alts: ["put up with"],
          a: "put up with",
          successTip: "😫 **PUT UP WITH!** Tolerate annoying behavior! Super common in workplace venting everywhere!"
        },
        {
          type: "mc",
          q: "**Laura:** 'We used to be close, but we ___ after college.'",
          options: ["grew apart", "fell out", "caught up", "looked up"],
          a: "grew apart",
          successTip: "🍂 **GREW APART!** Natural relationship drift! Happens in friendships worldwide as life changes!"
        },
        {
          type: "order",
          q: "**Traduce:** 'Él piensa que es mejor que todos los demás.'",
          words: ["He", "looks", "down", "on", "everyone", "else"],
          extra: ["up", "at", "for"],
          a: ["He", "looks", "down", "on", "everyone", "else"],
          successTip: "💡 **LOOK DOWN ON + person** = express superiority attitude! Common in describing workplace bullies!"
        },
        {
          type: "mc",
          q: "**Emma:** 'I can't ___ his rudeness any longer. I'm quitting!'",
          options: ["put up with", "get along with", "look up to", "catch up with"],
          a: "put up with",
          successTip: "🚪 **PUT UP WITH!** Reaches breaking point! Used in resignation conversations worldwide!"
        },
        {
          type: "input",
          q: "**Rachel:** 'Sarah and I ___ ___ ___ after she betrayed my trust.'",
          alts: ["fell out with"],
          a: "fell out with",
          successTip: "💥 **FELL OUT WITH!** Betrayal language! Common in personal conflict stories globally!"
        },
        {
          type: "mc",
          q: "**Manager:** 'Don't ___ your team members. Treat everyone with respect.'",
          options: ["look down on", "look up to", "catch up with", "get along with"],
          a: "look down on",
          successTip: "👔 **LOOK DOWN ON!** Professional advice! Common in leadership training worldwide!"
        },
        {
          type: "mc",
          q: "**John:** 'After years of living in different cities, we just ___ naturally.'",
          options: ["grew apart", "fell out", "looked up", "kept up"],
          a: "grew apart",
          successTip: "🌍 **GREW APART!** Distance effect! Reality for international friendships everywhere!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 💑 SET 3: Romantic Relationships (B1)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u5-s3",
      title: "Set 3: Romantic Relationships",
      icon: "💑",
      context: `**Dating & Romance - Love Life Phrasal Verbs**

Phrasal verbs específicos para relaciones románticas: comenzar, terminar, reconciliarse, y superar.

**Dating & Romance - Love Life Phrasal Verbs**

Specific phrasal verbs for romantic relationships: starting, ending, making up, and moving on.`,

      questions: [
        {
          type: "match",
          q: "**Match: Romance Phrasal Verbs → Meanings**",
          pairs: [
            { left: "break up with someone", right: "end a romantic relationship" },
            { left: "make up with someone", right: "reconcile after argument" },
            { left: "get over someone", right: "stop feeling sad about them" },
            { left: "ask someone out", right: "invite on a date" }
          ],
          successTip: "💑 **ROMANCE VOCAB!** Dating language used worldwide! Essential for social life in English-speaking countries!"
        },
        {
          type: "mc",
          q: "**Lisa:** 'After three years together, Mark ___ me. I'm heartbroken.'",
          options: ["broke up with", "made up with", "got over", "looked up to"],
          a: "broke up with",
          successTip: "💔 **BROKE UP WITH!** Relationship endings! Common in personal stories everywhere!"
        },
        {
          type: "mc",
          q: "**Tom:** 'We had a big fight, but we ___ the next day.'",
          options: ["made up", "broke up", "fell out", "grew apart"],
          a: "made up",
          successTip: "💕 **MADE UP!** Reconciliation! Healthy relationships have this everywhere in the world!"
        },
        {
          type: "input",
          q: "**Sarah:** 'It took me six months to ___ ___ my ex-boyfriend.'",
          alts: ["get over"],
          a: "get over",
          successTip: "🌈 **GET OVER!** Moving on process! Universal experience in dating culture worldwide!"
        },
        {
          type: "mc",
          q: "**Daniel:** 'I want to ___ that girl ___, but I'm nervous.'",
          options: ["ask / out", "break / up", "make / up", "get / over"],
          a: "ask / out",
          successTip: "😊 **ASK OUT!** Dating initiation! Nerve-wracking everywhere in the world!"
        },
        {
          type: "order",
          q: "**Traduce:** 'Ella terminó con su novio la semana pasada.'",
          words: ["She", "broke", "up", "with", "her", "boyfriend", "last", "week"],
          extra: ["made", "got", "over"],
          a: ["She", "broke", "up", "with", "her", "boyfriend", "last", "week"],
          successTip: "💡 **BREAK UP WITH + person** = end romantic relationship! Common breakup language!"
        },
        {
          type: "mc",
          q: "**Emma:** 'After our argument, I need to ___ him. I miss him.'",
          options: ["make up with", "break up with", "get over", "fall out with"],
          a: "make up with",
          successTip: "💝 **MAKE UP WITH!** Repair after fight! Shows emotional maturity in relationships globally!"
        },
        {
          type: "input",
          q: "**Mike:** 'I finally ___ ___ my ex. I'm ready to date again!'",
          alts: ["got over"],
          a: "got over",
          successTip: "✨ **GOT OVER!** Emotional recovery! Positive milestone in moving forward anywhere!"
        },
        {
          type: "mc",
          q: "**Rachel:** 'He wants to ___ me ___, but I'm not interested.'",
          options: ["ask / out", "break / up", "look / down", "put / up"],
          a: "ask / out",
          successTip: "🙅 **ASK OUT!** Dating rejection! Happens in dating scenes worldwide - always be kind!"
        },
        {
          type: "mc",
          q: "**John:** 'They fight and ___ every week. It's exhausting to watch.'",
          options: ["make up", "break up", "fall out", "grow apart"],
          a: "make up",
          successTip: "🎭 **MAKE UP!** Toxic cycle! Drama-filled relationships exist everywhere - red flag!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🛡️ SET 4: Standing Up & Dealing With (B1)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u5-s4",
      title: "Set 4: Standing Up & Dealing With",
      icon: "🛡️",
      context: `**Workplace Assertiveness - Defending & Tolerating**

Phrasal verbs para defender a otros y lidiar con situaciones difíciles en el trabajo y la vida.

**Workplace Assertiveness - Defending & Tolerating**

Phrasal verbs for defending others and dealing with difficult situations at work and in life.`,

      questions: [
        {
          type: "match",
          q: "**Match: Assertiveness Verbs → Meanings**",
          pairs: [
            { left: "stand up for someone", right: "defend them" },
            { left: "put up with something", right: "tolerate annoyance" },
            { left: "stand up to someone", right: "confront a bully" },
            { left: "take it out on someone", right: "blame them unfairly" }
          ],
          successTip: "🛡️ **ASSERTIVENESS LANGUAGE!** Essential for workplace conflicts! Important in professional environments worldwide!"
        },
        {
          type: "mc",
          q: "**Manager:** 'You need to ___ yourself when clients are rude.'",
          options: ["stand up for", "put up with", "get along with", "look down on"],
          a: "stand up for",
          successTip: "💪 **STAND UP FOR!** Self-advocacy! Critical skill in workplaces globally!"
        },
        {
          type: "mc",
          q: "**Sarah:** 'I won't ___ his disrespectful behavior anymore.'",
          options: ["put up with", "stand up for", "get along with", "look up to"],
          a: "put up with",
          successTip: "🚫 **PUT UP WITH!** Setting boundaries! Healthy workplace behavior everywhere!"
        },
        {
          type: "input",
          q: "**Tom:** 'Thank you for ___ ___ ___ me in the meeting. I appreciate it.'",
          alts: ["standing up for"],
          a: "standing up for",
          successTip: "🙏 **STANDING UP FOR!** Ally behavior! Valued in inclusive workplaces worldwide!"
        },
        {
          type: "mc",
          q: "**Emma:** 'She had a bad day and ___ it ___ me. It wasn't fair.'",
          options: ["took / out on", "stood / up for", "put / up with", "got / along with"],
          a: "took / out on",
          successTip: "😓 **TOOK IT OUT ON!** Misdirected anger! Happens in stressful work environments globally!"
        },
        {
          type: "order",
          q: "**Traduce:** 'Necesitas defender a tu equipo.'",
          words: ["You", "need", "to", "stand", "up", "for", "your", "team"],
          extra: ["down", "with", "at"],
          a: ["You", "need", "to", "stand", "up", "for", "your", "team"],
          successTip: "💡 **STAND UP FOR + person/group** = protect and defend! Leadership quality!"
        },
        {
          type: "mc",
          q: "**Daniel:** 'I finally ___ my bully boss and told him his behavior was unacceptable.'",
          options: ["stood up to", "looked up to", "put up with", "got along with"],
          a: "stood up to",
          successTip: "⚡ **STOOD UP TO!** Confronting power! Brave action in toxic workplaces worldwide!"
        },
        {
          type: "input",
          q: "**HR Manager:** 'We don't ___ ___ ___ harassment here. Report it immediately.'",
          alts: ["put up with"],
          a: "put up with",
          successTip: "🏢 **PUT UP WITH!** Zero tolerance policies! Standard in modern workplaces globally!"
        },
        {
          type: "mc",
          q: "**Mike:** 'Don't ___ your stress ___ your team. It's not their fault.'",
          options: ["take / out on", "stand / up for", "look / down on", "get / along with"],
          a: "take / out on",
          successTip: "🧘 **TAKE IT OUT ON!** Emotional management! Professional behavior expected everywhere!"
        },
        {
          type: "mc",
          q: "**Rachel:** 'Good leaders always ___ their team members when they're criticized unfairly.'",
          options: ["stand up for", "look down on", "put up with", "fall out with"],
          a: "stand up for",
          successTip: "👔 **STAND UP FOR!** Leadership trait! Valued in management roles worldwide!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🎯 SET 5: Ideas & Solutions (B1)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u5-s5",
      title: "Set 5: Ideas & Solutions",
      icon: "🎯",
      context: `**Creative Workplace - Innovation & Problem-Solving**

Phrasal verbs para hablar de ideas, soluciones, y cuando las cosas se acaban.

**Creative Workplace - Innovation & Problem-Solving**

Phrasal verbs for talking about ideas, solutions, and when things run out.`,

      questions: [
        {
          type: "match",
          q: "**Match: Creative Phrasal Verbs → Meanings**",
          pairs: [
            { left: "come up with an idea", right: "think of a solution" },
            { left: "run out of something", right: "have none left" },
            { left: "end up doing something", right: "finally do it" },
            { left: "go along with a plan", right: "agree to it" }
          ],
          successTip: "🎯 **PROBLEM-SOLVING LANGUAGE!** Business meetings worldwide use these! Creative industries love them!"
        },
        {
          type: "mc",
          q: "**Boss:** 'Great work! You ___ a brilliant solution to our problem.'",
          options: ["came up with", "ran out of", "looked up to", "put up with"],
          a: "came up with",
          successTip: "💡 **CAME UP WITH!** Innovation praise! Common in brainstorming sessions worldwide!"
        },
        {
          type: "mc",
          q: "**Sarah:** 'We ___ coffee this morning. Can someone buy more?'",
          options: ["ran out of", "came up with", "put up with", "got along with"],
          a: "ran out of",
          successTip: "☕ **RAN OUT OF!** Office supply crisis! Happens in workplaces everywhere!"
        },
        {
          type: "input",
          q: "**Team Leader:** 'Can anyone ___ ___ ___ a better marketing strategy?'",
          alts: ["come up with"],
          a: "come up with",
          successTip: "🚀 **COME UP WITH!** Creative challenge! Essential in marketing meetings globally!"
        },
        {
          type: "mc",
          q: "**Tom:** 'After hours of discussion, we ___ agreeing with his proposal.'",
          options: ["ended up", "ran out", "came up", "looked up"],
          a: "ended up",
          successTip: "🎭 **ENDED UP!** Final decision! Common in describing meeting outcomes worldwide!"
        },
        {
          type: "order",
          q: "**Traduce:** 'Se nos acabó el tiempo para terminar el proyecto.'",
          words: ["We", "ran", "out", "of", "time", "to", "finish", "the", "project"],
          extra: ["came", "ended", "went"],
          a: ["We", "ran", "out", "of", "time", "to", "finish", "the", "project"],
          successTip: "💡 **RUN OUT OF + resource** = deplete completely! Common deadline stress language!"
        },
        {
          type: "mc",
          q: "**Emma:** 'I don't agree, but I'll ___ your plan for now.'",
          options: ["go along with", "come up with", "run out of", "stand up for"],
          a: "go along with",
          successTip: "🤝 **GO ALONG WITH!** Reluctant agreement! Diplomatic language in business worldwide!"
        },
        {
          type: "input",
          q: "**Manager:** 'The printer ___ ___ ___ ink during the big presentation!'",
          alts: ["ran out of"],
          a: "ran out of",
          successTip: "🖨️ **RAN OUT OF!** Murphy's Law! Technology fails at worst times everywhere!"
        },
        {
          type: "mc",
          q: "**Daniel:** 'We need to ___ a new approach. The old one isn't working.'",
          options: ["come up with", "run out of", "put up with", "get along with"],
          a: "come up with",
          successTip: "🔄 **COME UP WITH!** Pivot moment! Critical in adaptive businesses globally!"
        },
        {
          type: "mc",
          q: "**Rachel:** 'We debated for hours and ___ choosing the cheaper option.'",
          options: ["ended up", "came up", "ran out", "stood up"],
          a: "ended up",
          successTip: "💰 **ENDED UP!** Budget decisions! Common in financial meetings worldwide!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🎊 SET 6: Anticipation & Follow-Through (B1+)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u5-s6",
      title: "Set 6: Anticipation & Plans",
      icon: "🎊",
      context: `**Planning & Excitement - Future Events**

Phrasal verbs para expresar ilusión por eventos futuros y hablar de seguir adelante con planes.

**Planning & Excitement - Future Events**

Phrasal verbs for expressing excitement about future events and talking about following through with plans.`,

      questions: [
        {
          type: "match",
          q: "**Match: Future-Oriented Verbs → Meanings**",
          pairs: [
            { left: "look forward to something", right: "be excited about it" },
            { left: "go through with something", right: "complete it despite difficulty" },
            { left: "call off something", right: "cancel it" },
            { left: "put off something", right: "postpone it" }
          ],
          successTip: "🎊 **PLANNING LANGUAGE!** Event management everywhere! Used in project planning globally!"
        },
        {
          type: "mc",
          q: "**Lisa:** 'I'm really ___ my vacation next month. I need a break!'",
          options: ["looking forward to", "going through with", "putting up with", "running out of"],
          a: "looking forward to",
          successTip: "✈️ **LOOKING FORWARD TO!** Vacation excitement! Universal feeling worldwide!"
        },
        {
          type: "mc",
          q: "**Manager:** 'Despite the challenges, we're going to ___ the project.'",
          options: ["go through with", "look forward to", "run out of", "come up with"],
          a: "go through with",
          successTip: "💪 **GO THROUGH WITH!** Determination! Shows commitment in business worldwide!"
        },
        {
          type: "input",
          q: "**Tom:** 'The wedding was ___ ___ due to bad weather.'",
          alts: ["called off", "cancelled"],
          a: "called off",
          successTip: "🌧️ **CALLED OFF!** Event cancellation! Happens with outdoor events everywhere!"
        },
        {
          type: "mc",
          q: "**Sarah:** 'Can we ___ the meeting ___ until tomorrow? I'm not ready.'",
          options: ["put / off", "call / off", "look / forward", "go / through"],
          a: "put / off",
          successTip: "📅 **PUT OFF!** Postponement! Common in flexible work schedules globally!"
        },
        {
          type: "order",
          q: "**Traduce:** 'Tengo muchas ganas de verte este fin de semana.'",
          words: ["I'm", "really", "looking", "forward", "to", "seeing", "you", "this", "weekend"],
          extra: ["going", "putting", "calling"],
          a: ["I'm", "really", "looking", "forward", "to", "seeing", "you", "this", "weekend"],
          successTip: "💡 **LOOK FORWARD TO + -ING** = express anticipation! Note gerund form!"
        },
        {
          type: "mc",
          q: "**Emma:** 'I was nervous, but I ___ the presentation anyway.'",
          options: ["went through with", "called off", "put off", "looked forward to"],
          a: "went through with",
          successTip: "🎤 **WENT THROUGH WITH!** Overcoming fear! Courage shown worldwide!"
        },
        {
          type: "input",
          q: "**Boss:** 'We had to ___ ___ the launch due to technical problems.'",
          alts: ["call off", "cancel"],
          a: "call off",
          successTip: "🚫 **CALL OFF!** Strategic cancellation! Better than launching broken products!"
        },
        {
          type: "mc",
          q: "**Mike:** 'Don't ___ important decisions ___. Deal with them now.'",
          options: ["put / off", "call / off", "look / forward", "go / through"],
          a: "put / off",
          successTip: "⏰ **PUT OFF!** Procrastination advice! Time management tip used everywhere!"
        },
        {
          type: "mc",
          q: "**Rachel:** 'Everyone is ___ the company party next Friday!'",
          options: ["looking forward to", "going through with", "putting off", "running out of"],
          a: "looking forward to",
          successTip: "🎉 **LOOKING FORWARD TO!** Social excitement! Team morale booster globally!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🌊 SET 7: Relationship Changes Over Time (B1+)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u5-s7",
      title: "Set 7: Changing Relationships",
      icon: "🌊",
      context: `**Life Changes - Relationship Evolution**

La vida cambia y las relaciones también. Phrasal verbs para describir cómo las conexiones evolucionan con el tiempo.

**Life Changes - Relationship Evolution**

Life changes and so do relationships. Phrasal verbs for describing how connections evolve over time.`,

      questions: [
        {
          type: "match",
          q: "**Match: Relationship Change Verbs → Meanings**",
          pairs: [
            { left: "grow apart from someone", right: "slowly become distant" },
            { left: "drift apart from someone", right: "naturally become less close" },
            { left: "settle down", right: "start a stable life" },
            { left: "warm up to someone", right: "start to like them" }
          ],
          successTip: "🌊 **LIFE TRANSITIONS!** Relationships evolve everywhere! Natural part of life globally!"
        },
        {
          type: "mc",
          q: "**Lisa:** 'After college, my roommate and I just ___ naturally.'",
          options: ["drifted apart", "looked up", "broke up", "made up"],
          a: "drifted apart",
          successTip: "🍃 **DRIFTED APART!** College friendship reality! Happens post-graduation worldwide!"
        },
        {
          type: "mc",
          q: "**Tom:** 'When I moved abroad, I ___ my childhood friends.'",
          options: ["grew apart from", "got along with", "looked up to", "caught up with"],
          a: "grew apart from",
          successTip: "🌍 **GREW APART FROM!** Immigrant experience! Distance affects friendships everywhere!"
        },
        {
          type: "input",
          q: "**Sarah:** 'He's ready to ___ ___ and start a family.'",
          alts: ["settle down"],
          a: "settle down",
          successTip: "🏡 **SETTLE DOWN!** Life milestone! Marriage and family planning language worldwide!"
        },
        {
          type: "mc",
          q: "**Emma:** 'At first I didn't like her, but I'm starting to ___ her.'",
          options: ["warm up to", "look down on", "fall out with", "run out of"],
          a: "warm up to",
          successTip: "😊 **WARM UP TO!** Changing first impressions! Happens in workplaces globally!"
        },
        {
          type: "order",
          q: "**Traduce:** 'Nos distanciamos después de la universidad.'",
          words: ["We", "grew", "apart", "after", "university"],
          extra: ["drifted", "looked", "broke"],
          a: ["We", "grew", "apart", "after", "university"],
          successTip: "💡 **GROW APART** = gradual distance! Common life transition phrase!"
        },
        {
          type: "mc",
          q: "**Daniel:** 'My brother finally ___ after years of traveling the world.'",
          options: ["settled down", "grew apart", "looked up", "broke up"],
          a: "settled down",
          successTip: "✈️ **SETTLED DOWN!** End of adventure phase! Adulting moment everywhere!"
        },
        {
          type: "input",
          q: "**Mike:** 'Living in different countries, we naturally ___ ___.'",
          alts: ["drifted apart", "grew apart"],
          a: "drifted apart",
          successTip: "🌏 **DRIFTED APART!** International life reality! Long-distance friendship challenge!"
        },
        {
          type: "mc",
          q: "**Rachel:** 'The new manager seemed strict, but the team is ___ him now.'",
          options: ["warming up to", "looking down on", "falling out with", "putting up with"],
          a: "warming up to",
          successTip: "👔 **WARMING UP TO!** Workplace adaptation! New boss adjustment period worldwide!"
        },
        {
          type: "mc",
          q: "**John:** 'After traveling for 10 years, I'm ready to ___ and buy a house.'",
          options: ["settle down", "grow apart", "drift apart", "break up"],
          a: "settle down",
          successTip: "🏠 **SETTLE DOWN!** Homeownership decision! Major life step everywhere!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🏢 SET 8: Workplace Mixed Contexts (B2)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u5-s8",
      title: "Set 8: Complex Workplace Dynamics",
      icon: "🏢",
      context: `**Professional Environment - Multiple Phrasal Verbs**

Situaciones laborales complejas que requieren múltiples phrasal verbs. Conversaciones reales de oficina.

**Professional Environment - Multiple Phrasal Verbs**

Complex workplace situations requiring multiple phrasal verbs. Real office conversations.`,

      questions: [
        {
          type: "mc",
          q: "**Review meeting:** 'Sarah ___ a great idea, but we ___ time to implement it before the deadline.'",
          options: ["came up with / ran out of", "looked up to / put up with", "broke up with / made up with", "fell out with / got along with"],
          a: "came up with / ran out of",
          successTip: "💼 **BUSINESS REALITY!** Good ideas + tight deadlines! Common in fast-paced companies!"
        },
        {
          type: "mc",
          q: "**HR complaint:** 'I ___ my manager, but I can't ___ his aggressive communication style anymore.'",
          options: ["look up to / put up with", "get along with / stand up for", "fall out with / come up with", "break up with / get over"],
          a: "look up to / put up with",
          successTip: "😓 **MIXED FEELINGS!** Respecting skills but hating behavior! Workplace dilemma everywhere!"
        },
        {
          type: "order",
          q: "**Traduce:** 'Ella defendió a su equipo cuando el jefe los criticó.'",
          words: ["She", "stood", "up", "for", "her", "team", "when", "the", "boss", "criticized", "them"],
          extra: ["looked", "put", "got"],
          a: ["She", "stood", "up", "for", "her", "team", "when", "the", "boss", "criticized", "them"],
          successTip: "💡 **LEADERSHIP MOMENT!** Protecting your team! Valued behavior globally!"
        },
        {
          type: "input",
          q: "**Team dynamics:** 'We used to ___ ___ ___ great, but we ___ ___ after the promotion conflict.'",
          alts: ["get along with", "fell out"],
          a: "get along with",
          successTip: "💔 **WORKPLACE CONFLICT!** Competition ruins friendships! Happens in offices worldwide!"
        },
        {
          type: "mc",
          q: "**Project planning:** 'I'm ___ the launch event, but I'm nervous we won't ___ it ___ if more problems arise.'",
          options: ["looking forward to / go through with", "putting up with / come up with", "getting along with / run out of", "breaking up with / make up with"],
          a: "looking forward to / go through with",
          successTip: "🎯 **CAUTIOUS OPTIMISM!** Excitement mixed with worry! Pre-launch nerves everywhere!"
        },
        {
          type: "mc",
          q: "**Office gossip:** 'He ___ everyone ___, takes credit for their work, and the CEO still ___ him!'",
          options: ["looks down on / looks up to", "gets along with / puts up with", "stands up for / falls out with", "comes up with / runs out of"],
          a: "looks down on / looks up to",
          successTip: "🎭 **TOXIC WORKPLACE!** Arrogant people getting ahead! Frustrating reality everywhere!"
        },
        {
          type: "input",
          q: "**Meeting postponement:** 'We need to ___ ___ the meeting because we ___ ___ ___ budget data.'",
          alts: ["put off", "ran out of"],
          a: "put off",
          successTip: "📊 **DATA DEPENDENCY!** Can't decide without information! Business logic worldwide!"
        },
        {
          type: "mc",
          q: "**Team building:** 'The new hires are starting to ___ the company culture, and they ___ everyone on the team now.'",
          options: ["warm up to / get along with", "look down on / put up with", "break up with / get over", "run out of / come up with"],
          a: "warm up to / get along with",
          successTip: "🎉 **SUCCESSFUL ONBOARDING!** Integration happening! Great team cohesion everywhere!"
        },
        {
          type: "mc",
          q: "**Difficult decision:** 'I don't ___ this plan, but I'll ___ it because the team voted for it.'",
          options: ["go along with / go along with", "look forward to / put off", "stand up for / look down on", "come up with / run out of"],
          a: "go along with / go along with",
          successTip: "🤝 **DEMOCRATIC PROCESS!** Accepting majority decision! Team dynamics worldwide!"
        },
        {
          type: "mc",
          q: "**Career reflection:** 'After they ___ my colleague unfairly, I ___ respect for management. We've all ___ from the company values.'",
          options: ["looked down on / ran out of / grown apart", "stood up for / came up with / settled down", "got along with / looked forward to / warmed up", "put up with / went through with / broke up"],
          a: "looked down on / ran out of / grown apart",
          successTip: "💼 **CULTURE DECAY!** Losing faith in leadership! Happens in changing companies worldwide!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🎭 SET 9: Social Life Advanced (B2)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u5-s9",
      title: "Set 9: Complex Social Situations",
      icon: "🎭",
      context: `**Social Dynamics - Friend Groups & Drama**

Situaciones sociales complejas: grupos de amigos, conflictos, reconciliaciones, y la vida social de adultos.

**Social Dynamics - Friend Groups & Drama**

Complex social situations: friend groups, conflicts, reconciliations, and adult social life.`,

      questions: [
        {
          type: "mc",
          q: "**Friend drama:** 'After Lisa ___ me in front of everyone, I ___ her. But now she wants to ___.'",
          options: ["looked down on / fell out with / make up", "stood up for / got along with / break up", "came up with / ran out of / settle down", "put up with / looked forward to / call off"],
          a: "looked down on / fell out with / make up",
          successTip: "😤 **FRIENDSHIP CRISIS!** Public humiliation! Happens in social groups everywhere!"
        },
        {
          type: "mc",
          q: "**Moving away:** 'I ___ seeing my best friend next month, but I'm sad we'll probably ___ when I move abroad.'",
          options: ["look forward to / grow apart", "put up with / make up", "come up with / run out of", "stand up for / fall out with"],
          a: "look forward to / grow apart",
          successTip: "🌍 **BITTERSWEET!** Last visit before big move! Expat emotional reality everywhere!"
        },
        {
          type: "order",
          q: "**Traduce:** 'Solíamos ser mejores amigos, pero nos distanciamos con el tiempo.'",
          words: ["We", "used", "to", "be", "best", "friends,", "but", "we", "drifted", "apart", "over", "time"],
          extra: ["grew", "broke", "made"],
          a: ["We", "used", "to", "be", "best", "friends,", "but", "we", "drifted", "apart", "over", "time"],
          successTip: "💡 **NATURAL DRIFT!** No fight, just life! Common adulthood experience!"
        },
        {
          type: "input",
          q: "**Party planning:** 'I'm ___ ___ ___ the reunion, but we might have to ___ it ___ if not enough people can come.'",
          alts: ["looking forward to", "call off"],
          a: "looking forward to",
          successTip: "🎊 **EVENT UNCERTAINTY!** RSVP problems! Party planning challenges everywhere!"
        },
        {
          type: "mc",
          q: "**New friend:** 'I didn't ___ her at first, but now I really ___ her. She's become my closest friend here.'",
          options: ["warm up to / look up to", "look down on / put up with", "fall out with / get over", "break up with / make up with"],
          a: "warm up to / look up to",
          successTip: "🌟 **FRIENDSHIP GROWTH!** From strangers to close! Beautiful connection development!"
        },
        {
          type: "mc",
          q: "**Group conflict:** 'Tom ___ the entire friend group, so we all ___ him. Now he wants back in, but we've all ___ and don't miss him.'",
          options: ["looked down on / fell out with / moved on", "stood up for / got along with / warmed up", "came up with / ran out of / settled down", "looked forward to / went through with / broke up"],
          a: "looked down on / fell out with / moved on",
          successTip: "🚪 **GROUP DECISION!** Toxic person removed! Healthy boundaries in friendships!"
        },
        {
          type: "input",
          q: "**Relationship advice:** 'You need to ___ ___ him completely before you can start dating again.'",
          alts: ["get over"],
          a: "get over",
          successTip: "💔 **HEALING TIME!** Can't rush moving on! Universal dating advice everywhere!"
        },
        {
          type: "mc",
          q: "**Busy adults:** 'We haven't ___ in months! Let's ___ it ___ until next week when we're both free.'",
          options: ["caught up / put off", "fallen out / call off", "broken up / make up", "grown apart / go through with"],
          a: "caught up / put off",
          successTip: "📅 **ADULT LIFE!** Finding time is hard! Busy schedules everywhere!"
        },
        {
          type: "mc",
          q: "**Supporting friends:** 'When everyone ___ Sarah for her career choice, I ___ her. Real friends support each other.'",
          options: ["looked down on / stood up for", "got along with / fell out with", "looked forward to / came up with", "put up with / ran out of"],
          a: "looked down on / stood up for",
          successTip: "💪 **TRUE FRIENDSHIP!** Defending unpopular choices! Loyalty shown everywhere!"
        },
        {
          type: "mc",
          q: "**Life stages:** 'All my friends are ___ and having kids, but I'm still ___ my ex and not ready for that life yet.'",
          options: ["settling down / getting over", "growing apart / looking forward to", "warming up / putting off", "coming up with / running out of"],
          a: "settling down / getting over",
          successTip: "🎭 **DIFFERENT TIMELINES!** Everyone's path differs! Life stage mismatches everywhere!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🏆 SET 10: Mastery - All Contexts (B2+)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u5-s10",
      title: "Set 10: Mastery - Real Life Stories",
      icon: "🏆",
      context: `**🏆 MASTERY LEVEL - Complex Narratives**

Situaciones de la vida real que requieren dominio completo de múltiples phrasal verbs. Historias complejas con emociones y contextos mixtos.

**🏆 MASTERY LEVEL - Complex Narratives**

Real-life situations requiring complete mastery of multiple phrasal verbs. Complex stories with mixed emotions and contexts.`,

      questions: [
        {
          type: "mc",
          q: "**CAREER STORY:** 'I always ___ my mentor, and I ___ everyone on my first team. But when I got promoted, some colleagues ___ me because they thought I was too young. I had to ___ myself constantly.'",
          options: ["looked up to / got along with / looked down on / stand up for", "fell out with / broke up with / made up with / get over", "came up with / ran out of / put off / call off", "looked forward to / went through with / settled down / warmed up to"],
          a: "looked up to / got along with / looked down on / stand up for",
          successTip: "🏢 **CAREER JOURNEY!** From mentee to leader! Age discrimination + self-advocacy!"
        },
        {
          type: "mc",
          q: "**RELATIONSHIP STORY:** 'We ___ in college, ___ after graduation, ___ three years later, dated briefly, then ___ when we realized we were better as friends.'",
          options: ["got along / grew apart / caught up / broke up", "looked up / fell out / made up / settled down", "stood up / came up / ran out / called off", "looked forward / went through / warmed up / put off"],
          a: "got along / grew apart / caught up / broke up",
          successTip: "💕 **FRIENDSHIP TO ROMANCE!** Full relationship cycle! Happens worldwide!"
        },
        {
          type: "order",
          q: "**Traduce historia compleja:** 'Se nos acabaron ideas, así que pospusimos la reunión hasta que alguien propusiera una solución.'",
          words: ["We", "ran", "out", "of", "ideas,", "so", "we", "put", "off", "the", "meeting", "until", "someone", "came", "up", "with", "a", "solution"],
          extra: ["looked", "got", "fell"],
          a: ["We", "ran", "out", "of", "ideas,", "so", "we", "put", "off", "the", "meeting", "until", "someone", "came", "up", "with", "a", "solution"],
          successTip: "💡 **BUSINESS PROBLEM-SOLVING!** Multiple phrasal verbs in one situation!"
        },
        {
          type: "mc",
          q: "**TOXIC WORKPLACE:** 'My boss ___ the entire team, never ___ us when clients complained, and we all ___ his micromanaging. Eventually, the whole team ___ against him and he was fired.'",
          options: ["looked down on / stood up for / put up with / stood up", "got along with / fell out with / looked forward to / warmed up", "came up with / ran out of / went through with / settled down", "broke up with / made up with / got over / drifted apart"],
          a: "looked down on / stood up for / put up with / stood up",
          successTip: "⚖️ **COLLECTIVE ACTION!** Team rebellion! Bad management consequences everywhere!"
        },
        {
          type: "input",
          q: "**FRIENDSHIP EVOLUTION:** 'We were inseparable, then ___ ___ when she moved, tried to ___ ___ annually but kept ___ it ___, and eventually just ___ ___ completely. No drama, just life.'",
          alts: ["grew apart", "catch up", "putting off", "drifted apart"],
          a: "grew apart",
          successTip: "🍂 **NATURAL ENDING!** No conflict, just distance! Mature friendship evolution!"
        },
        {
          type: "mc",
          q: "**PROJECT NIGHTMARE:** 'We ___ a brilliant plan, everyone was ___ the launch, but we ___ budget halfway through and had to ___ the whole thing ___. Heartbreaking.'",
          options: ["came up with / looking forward to / ran out of / call off", "looked up to / getting along with / put up with / put off", "fell out with / standing up for / grew apart from / go through with", "broke up with / getting over / warmed up to / settle down"],
          a: "came up with / looking forward to / ran out of / call off",
          successTip: "💔 **STARTUP REALITY!** Great idea + no funding! Common entrepreneurship story!"
        },
        {
          type: "mc",
          q: "**FAMILY DYNAMICS:** 'I always ___ my older brother, but as adults we ___. When our parents got sick, we had to ___ and work together. Now I realize I had to ___ him first to ___ him as a person.'",
          options: ["looked up to / grew apart / catch up / get over / look up to", "got along with / fell out with / make up / stand up for / put up with", "came up with / ran out of / put off / warm up to / settle down", "looked forward to / went through with / call off / break up with / drift apart"],
          a: "looked up to / grew apart / catch up / get over / look up to",
          successTip: "👨‍👦 **SIBLING MATURITY!** From hero-worship to real relationship! Family growth!"
        },
        {
          type: "mc",
          q: "**EXPAT EXPERIENCE:** 'I ___ moving abroad, but reality was hard. I ___ my home country friends, couldn't ___ the new culture, and almost gave up. But I ___ it ___, and now I ___ my new life here.'",
          options: ["looked forward to / grew apart from / warm up to / went through with / look forward to", "came up with / ran out of / put up with / put off / stand up for", "got along with / fell out with / look down on / call off / catch up with", "broke up with / made up with / get over / settle down / drift apart from"],
          a: "looked forward to / grew apart from / warm up to / went through with / look forward to",
          successTip: "🌏 **IMMIGRATION JOURNEY!** Full adaptation cycle! Real expat experience worldwide!"
        },
        {
          type: "mc",
          q: "**LEADERSHIP TEST:** 'When the CEO ___ my team unfairly, I ___ them even though it risked my career. Some executives ___ me for it, but my team ___ me and we ___ a solution that proved them all wrong.'",
          options: ["looked down on / stood up for / looked down on / looked up to / came up with", "got along with / fell out with / put up with / broke up with / ran out of", "looked forward to / went through with / warmed up to / grew apart from / put off", "broke up with / made up with / got over / settled down / called off"],
          a: "looked down on / stood up for / looked down on / looked up to / came up with",
          successTip: "👔 **CAREER DEFINING MOMENT!** Risk + loyalty + vindication! True leadership!"
        },
        {
          type: "mc",
          q: "**🏆 FINAL MASTERY:** 'I ___ my ex for a year, ___ coworkers I didn't click with, ___ old friends naturally, ___ new people slowly, finally ___, ___ someone special, and now I ___ every day of my new life. Growth takes time.'",
          options: ["got over / put up with / drifted apart from / warmed up to / settled down / asked out / look forward to", "looked up to / stood up for / came up with / ran out of / went through with / put off / called off", "fell out with / broke up with / made up with / grew apart from / caught up with / looked down on / got along with", "looked forward to / went through with / warmed up to / put up with / came up with / ran out of / stood up for"],
          a: "got over / put up with / drifted apart from / warmed up to / settled down / asked out / look forward to",
          successTip: "🎉 **INCREDIBLE!** You've mastered the COMPLETE phrasal verb journey! From heartbreak to workplace tolerance, natural friendship drift, slow trust building, life stability, romantic courage, and daily gratitude. You can now express the full complexity of human relationships in English! **CONGRATULATIONS - UNIT 4 PHRASAL VERBS COMPLETE!** 🏆✨ You sound like a native English speaker discussing relationships!"
        }
      ]
    }
  ]
};
